import React from 'react';
import { ChefHat, Bath, BedDouble, Hammer, Check } from 'lucide-react';
import { services } from '../mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const iconMap = {
  ChefHat: ChefHat,
  Bath: Bath,
  BedDouble: BedDouble,
  Hammer: Hammer
};

const Services = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Professional installation services tailored to transform your home with quality and precision
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const objectPosition = service.imagePosition === 'top' ? 'top' : 
                                  service.imagePosition === 'bottom' ? 'bottom' : 'center';
            return (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-amber-500 cursor-pointer overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader className="p-0">
                  {service.image ? (
                    <div className="w-full h-48 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        style={{ objectPosition: objectPosition }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-amber-100 flex items-center justify-center group-hover:bg-amber-500 transition-colors duration-300">
                      <Icon className="text-amber-600 group-hover:text-white transition-colors duration-300" size={48} />
                    </div>
                  )}
                </CardHeader>
                <CardHeader className="pt-6">
                  <CardTitle className="text-2xl text-slate-900">{service.title}</CardTitle>
                  <CardDescription className="text-base mt-2">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-slate-700">
                        <Check size={18} className="text-amber-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={scrollToContact}
                    variant="outline" 
                    className="w-full mt-6 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white transition-colors"
                  >
                    Get Quote
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-slate-50 rounded-2xl p-8 md:p-12 mt-16">
          <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Why Choose Imperial Craft?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">✓</span>
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Expert Craftsmanship</h4>
              <p className="text-slate-600 text-sm">Years of experience delivering excellence</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">★</span>
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Quality Materials</h4>
              <p className="text-slate-600 text-sm">Premium fittings and lasting durability</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">£</span>
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Competitive Pricing</h4>
              <p className="text-slate-600 text-sm">Excellent value without compromise</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl font-bold">⏰</span>
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Reliable Service</h4>
              <p className="text-slate-600 text-sm">On time and on budget delivery</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;